import { Box } from "@mui/system";
import { BarChart, LineChart } from "@mui/x-charts";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { Card, CardContent, CardProps } from "@pimo/pimo-components";
import { BffOe, OeKpis } from "aint-types";
import { KPI_LABELS } from "aint-utils";
import { useState } from "react";

import { getOeKpiChartSeries } from "../../app/utils";
import { OE_KPIS_DATA_GRID_ROWS } from "../oe-kpis-data-grid/oe-kpis-data-grid-constants";
import { OeKpiChartSelect } from "./oe-kpi-chart-select";
import { OeKpiChartTypeSwitch } from "./oe-kpi-chart-type-switch";

export type Scale = "Q1" | "Q2" | "Q3" | "Q4" | "full";

export type ChartType = "line" | "bar";

export type ChartSeries = {
  id: string;
  data: number[];
  label: string;
  curve?: "linear" | "natural";
  color?: string;
};

export type OeKpiCartCardProps = {
  cardProps?: CardProps;
  operatingEntity?: BffOe;
};

export const OeKpiCartCard: PimoReactComponent<OeKpiCartCardProps> = ({
  cardProps,
  operatingEntity,
}) => {
  const [chartType, setChartType] = useState<ChartType>("line");
  const [scale, setScale] = useState<Scale>("full");
  const [selectedKpi, setSelectedKpi] = useState<keyof OeKpis>("policies");
  const kpiLabel = KPI_LABELS[selectedKpi]?.label ?? "";

  if (!operatingEntity) {
    return <>Loading</>;
  }

  const { labels, series } = getOeKpiChartSeries({
    operatingEntity,
    selectedKpi,
    scale,
  });

  return (
    <Card
      {...cardProps}
      title={`${kpiLabel} from 2023 to 2030 (Plan vs Actual)`}
      rightSlot={
        <Box sx={{ display: "flex", gap: 2 }}>
          <OeKpiChartSelect<Scale>
            value={scale}
            label="Timeframe"
            setValue={setScale}
            items={[
              { id: "full", value: "full", label: "Full Program Cycle" },
              { id: "q1", value: "Q1", label: "Q1" },
              { id: "q2", value: "Q2", label: "Q2" },
              { id: "q3", value: "Q3", label: "Q3" },
              { id: "q4", value: "Q4", label: "Q4" },
            ]}
          />
          <OeKpiChartSelect<keyof OeKpis>
            value={selectedKpi}
            setValue={setSelectedKpi}
            label="KPI"
            items={OE_KPIS_DATA_GRID_ROWS.map(({ kpi, field }) => ({
              id: field,
              label: KPI_LABELS[field].label ?? kpi,
              value: field,
            }))}
          />
        </Box>
      }
    >
      <CardContent>
        <Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <OeKpiChartTypeSwitch
              value={chartType}
              setChartType={setChartType}
            />
          </Box>
          {chartType === "line" && (
            <LineChart
              height={350}
              margin={{
                bottom: 100,
              }}
              series={series}
              xAxis={[
                {
                  scaleType: "band",
                  data: labels,
                  tickPlacement: "middle",
                  tickLabelPlacement: "middle",
                },
              ]}
              slotProps={{
                legend: {
                  position: { vertical: "bottom", horizontal: "right" },
                },
              }}
            />
          )}
          {chartType === "bar" && (
            <BarChart
              height={350}
              margin={{
                bottom: 100,
              }}
              grid={{ horizontal: true }}
              series={series}
              xAxis={[
                {
                  scaleType: "band",
                  data: labels,
                  tickPlacement: "middle",
                  tickLabelPlacement: "middle",
                },
              ]}
              slotProps={{
                legend: {
                  position: { vertical: "bottom", horizontal: "right" },
                },
              }}
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
